import { usePlanetStore } from '@/stores/planetStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { logger } from '@/models/logger';

export default class Eta {

  private startX: number;
  private startY: number;

  constructor() {
    this.startX = 0;
    this.startY = 0;
  }

  start(x: number, y: number) {
    //console.log('Eta start', x, y);
    this.startX = x;
    this.startY = y;
  }

  etaNext(targetX: number, targetY: number, speed: number): number {
    //console.log('Eta etaNext',this.startX,this.startY, targetX, targetY, speed);

    // Use the backend's complex movement logic instead of simple distance/speed
    const eta = this.calculateMovementETA(this.startX, this.startY, targetX, targetY, speed);

    // Store for next iteration
    this.startX = targetX;
    this.startY = targetY;

    return eta;
  }

  /**
   * Calculate ETA using the same movement logic as the backend
   * This accounts for speed limiting, direction normalization, and multi-turn movement
   */
  private calculateMovementETA(currentX: number, currentY: number, targetX: number, targetY: number, maxSpeed: number): number {
    if (maxSpeed <= 0) return 0;
    
    let turns = 0;
    let x = currentX;
    let y = currentY;
    
    // Continue until we reach the target (exact integer match)
    while (x !== targetX || y !== targetY) {
      // Calculate total distance to target
      const distance = Math.sqrt(Math.pow(targetX - x, 2) + Math.pow(targetY - y, 2));
      
      if (distance === 0) break; // Already at target
      
      // Speed limiting: steps = min(maxSpeed, distance)
      const steps = Math.min(maxSpeed, distance);
      
      // Direction normalization
      const directionX = (targetX - x) / distance;
      const directionY = (targetY - y) / distance;
      
      let newX: number, newY: number;
      
      // Special handling for speed=1 ships - move on axis with longest distance to target
      if (maxSpeed === 1) {
        const distanceX = Math.abs(targetX - x);
        const distanceY = Math.abs(targetY - y);
        
        if (distanceX > distanceY) {
          // Move in X direction
          newX = x + (targetX > x ? 1 : -1);
          newY = y;
        } else if (distanceY > distanceX) {
          // Move in Y direction
          newX = x;
          newY = y + (targetY > y ? 1 : -1);
        } else {
          // Equal distances, move diagonally
          newX = x + (targetX > x ? 1 : -1);
          newY = y + (targetY > y ? 1 : -1);
        }
      } else {
        // For higher speeds, use movement vectors
        const moveX = directionX * steps;
        const moveY = directionY * steps;
        
        // Round to nearest integer
        newX = x + Math.round(moveX);
        newY = y + Math.round(moveY);
        
        // Overshoot prevention logic - matches backend exactly
        if (Math.abs(newX - targetX) < Math.abs(x - targetX)) {
          newX = targetX;
        }
        if (Math.abs(newY - targetY) < Math.abs(y - targetY)) {
          newY = targetY;
        }
        
        // Validate actual distance moved doesn't exceed maxSpeed
        const actualDistanceMoved = Math.sqrt(Math.pow(newX - x, 2) + Math.pow(newY - y, 2));
        if (actualDistanceMoved > maxSpeed) {
          const scale = maxSpeed / actualDistanceMoved;
          newX = x + Math.round((newX - x) * scale);
          newY = y + Math.round((newY - y) * scale);
        }
      }
      
      x = newX;
      y = newY;
      turns++;
      
      // Safety check to prevent infinite loops
      if (turns > 499) {
        logger.warn('ETA calculation exceeded maximum iterations, returning approximate value');
        break;
      }
    }
    
    return turns;
  }

  techETA(techPoints:number): number {
    const planetStore = usePlanetStore();
    const gameStore = useGameStore();
    //console.log('Eta techETA',techPoints);
    // Get current science output
    const scienceOutput = planetStore.getTotalScienceOutput()
    // Calculate the time to research the tech
    return Math.ceil((techPoints - (gameStore.player?.sciencePoints ?? 0)) / scienceOutput);
  }
}
